// dialogs/ResourceDialog/container.js - ResourceDialog container logic
import React, { useState, useEffect } from 'react';
import ResourceDialogView from './view';

const ResourceDialogContainer = ({ 
  open, 
  onClose, 
  onSubmit, 
  resource, 
  workAreas, 
  resourceTypes, 
  isEditing = false
}) => {
  // Form state
  const [currentResource, setCurrentResource] = useState({
    name: '',
    resource_type: 'MR',
    work_area: workAreas?.length > 0 ? workAreas[0].id : null,
    capacity: 4,
    status: 'IN',
    ...resource
  });
  
  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens/closes or resource changes
  useEffect(() => {
    if (open) {
      setCurrentResource({
        name: '',
        resource_type: 'MR',
        work_area: workAreas?.length > 0 ? workAreas[0].id : null,
        capacity: 4,
        status: 'IN',
        ...resource
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open, resource, workAreas]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!currentResource.name || currentResource.name.trim().length === 0) {
      newErrors.name = 'Meeting room name is required';
    } else if (currentResource.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (currentResource.name.trim().length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    // Capacity validation
    if (!currentResource.capacity || currentResource.capacity < 1) {
      newErrors.capacity = 'Capacity must be at least 1';
    } else if (currentResource.capacity > 50) {
      newErrors.capacity = 'Capacity cannot exceed 50';
    }

    // Work area validation
    if (!currentResource.work_area) {
      newErrors.work_area = 'Work area is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid (for button state)
  const isFormValid = () => {
    return (
      currentResource.name && 
      currentResource.name.trim().length >= 2 &&
      currentResource.work_area && 
      currentResource.capacity && 
      currentResource.capacity >= 1 && 
      currentResource.capacity <= 50
    );
  };

  // Handle form field changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // Convert capacity to number
    const processedValue = name === 'capacity' ? Number(value) : value;
    
    setCurrentResource(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Always ensure resource_type is set to MR for meeting rooms 
      // and status is inactive for new rooms
      const updatedResource = {
        ...currentResource,
        resource_type: 'MR', // Meeting Room type is fixed
        status: 'IN',        // New rooms are always inactive until they meet requirements
        name: currentResource.name.trim(), // Trim whitespace
      };
      
      await onSubmit(updatedResource);
      // Dialog will close automatically on success via parent component
    } catch (error) {
      console.error('Error submitting form:', error);
      // Keep dialog open on error and show any validation errors
      // The parent component should handle error display via snackbar
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form state
      setCurrentResource({
        name: '',
        resource_type: 'MR',
        work_area: workAreas?.length > 0 ? workAreas[0].id : null,
        capacity: 4,
        status: 'IN'
      });
      setErrors({});
      setIsSubmitting(false);
      onClose();
    }
  };

  // Prepare props for view
  const viewProps = {
    // Dialog state
    open,
    onClose,
    isEditing,
    isSubmitting,
    
    // Form data
    currentResource,
    workAreas,
    resourceTypes,
    
    // Form validation
    errors,
    isFormValid: isFormValid(),
    
    // Handlers
    handleInputChange,
    handleSubmit,
    handleClose
  };

  return <ResourceDialogView {...viewProps} />;
};

export default ResourceDialogContainer;